import React from "react";
import { isEmpty } from "lodash";
import { type FormikProps } from "formik";
import ExpandableLinks from "components/ExpandableItems/ExpandableLinks/ExpandableLinks";
import MultiSelectWithAdditionField from "components/forms/MultiSelectWithAdditionField";
import { type NormalizedDropdown } from "utils/miscellaneousUtils";
import { ValidatedForm } from "components/forms";
import { type Link } from "components/ExpandableItems/types";
import { type TagsPayloadCamelPlural } from "interfaces/Tags";
import { type ExternalLinkSettings } from "../types/externalLinkTypes";

export interface SettingsFormProps {
  entityId: number;
  settings: ExternalLinkSettings;
  normalizedTags?: TagsPayloadCamelPlural;
  isDisabled: boolean;
  isLoading?: boolean;
  updateTags: (id: number, tags: string[], type: "labels" | "softwareApplications") => void;
  tagsFormik: FormikProps<{
    softwareApplications: string[];
    labels: string[];
    flows: Link[];
    packs: { id: number; name: string }[];
  }>;
}

const SettingsForm: React.FC<SettingsFormProps> = (props) => {
  const { entityId, settings, isLoading, tagsFormik } = props;
  const handleTagChange = async (
    field: "softwareApplications" | "labels",
    value: NormalizedDropdown,
    shouldValidate?: boolean,
  ) => {
    const errors = await tagsFormik.setFieldValue(field, value.selected, shouldValidate);
    if (isEmpty(errors?.[field])) {
      props.updateTags(entityId, value.selected, field);
    }
  };

  const renderFields = () => {
    const { isDisabled, normalizedTags } = props;
    return (
      <div>
        <div>
          <MultiSelectWithAdditionField
            {...tagsFormik}
            isCaseSensitive
            shouldValidate
            label="Software Application"
            propertyName="softwareApplications"
            disabled={isDisabled}
            options={normalizedTags?.softwareApplications}
            value={tagsFormik.values.softwareApplications}
            setFieldValue={handleTagChange}
          />
          <MultiSelectWithAdditionField
            {...tagsFormik}
            isCaseSensitive
            shouldValidate
            label="Labels"
            propertyName="labels"
            disabled={isDisabled}
            options={normalizedTags?.labels}
            value={tagsFormik.values.labels}
            setFieldValue={handleTagChange}
          />

          <ExpandableLinks
            label="Associated Flows"
            linkFormatter={(id) => `/content/flows/${id}/items`}
            iconName="fa-code-branch"
            links={settings.flows}
          />
        </div>
      </div>
    );
  };

  return (
    <ValidatedForm withoutFormik isLoading={isLoading} formWidthComputer={6} disablePreventTransitionPrompt>
      {renderFields}
    </ValidatedForm>
  );
};

export default SettingsForm;
